/**
 * HS classifier (moderator) — docs/decisions/hs-duty-tax.md part 2.
 *
 * Top: a playground that runs the classifier with each source's votes, to show
 * how the voting works. Below: every run (`GET /hscodes/moderator/runs/`) with
 * its inputs, votes, result and the code the requester chose. A moderator
 * corrects a run, or confirms it with the same code; corrected and confirmed
 * runs become precedents (the "precedent" voter) for similar products.
 */
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import HsClassifierPanel from "@/components/HsClassifier/HsClassifierPanel";
import ModeratorLayout from "@/components/ModeratorLayout/ModeratorLayout";
import { unwrapPaginated } from "@/config/api";
import { EMPTY_VALUE, formatDateTime, formatPercent } from "@/i18n/format";
import { getApiErrorMessage } from "@/lib/apiError";
import { hsDepthKey, isKnownVoter } from "@/lib/hsCodes";
import type { ClassificationRun, HscodesModeratorRunsRetrieveParams } from "@/services/moderators/generated/model";
import {
  getHscodesModeratorRunsRetrieveQueryKey,
  useHscodesModeratorRunsCorrectCreate,
  useHscodesModeratorRunsRetrieve,
} from "@/services/moderators/generated/moderators-hs-codes/moderators-hs-codes";
import "./ModeratorPage.css";
import "./Kyb/Kyb.css";

type Ballot = { weight: number; skipped: string; error: string; votes: { code: string; score: number }[] };
type RunFilter = "all" | "uncorrected" | "corrected";

const ModeratorHsClassifierPage = () => (
  <ModeratorLayout>
    <Content />
  </ModeratorLayout>
);

const Content = () => {
  const { t } = useTranslation(["moderator", "trade", "common"]);
  return (
    <div>
      <header className="mod-page__header">
        <div>
          <h1 className="mod-page__title">{t("hsClassifier.title")}</h1>
          <p className="mod-page__subtitle">{t("hsClassifier.subtitle")}</p>
        </div>
      </header>
      <div className="kyb-grid">
        <Playground />
        <RunLog />
      </div>
    </div>
  );
};

const Playground = () => {
  const { t } = useTranslation(["moderator", "common"]);
  const [draft, setDraft] = useState({ name: "", description: "", material: "", use: "" });
  const [submitted, setSubmitted] = useState<typeof draft | null>(null);
  const [runCount, setRunCount] = useState(0);
  const qc = useQueryClient();

  return (
    <section className="kyb-section">
      <h2 className="kyb-section__title">{t("hsClassifier.playground.title")}</h2>
      <p className="kyb-section__help">{t("hsClassifier.playground.help")}</p>
      <form
        className="kyb-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (!draft.name.trim()) return;
          setSubmitted({ ...draft });
          setRunCount((n) => n + 1);
        }}
      >
        <div className="kyb-form__grid">
          {(["name", "material", "use"] as const).map((field) => (
            <label key={field}>
              {t(`hsClassifier.playground.fields.${field}`)}
              <input
                className="mod-page__input"
                value={draft[field]}
                onChange={(e) => setDraft({ ...draft, [field]: e.target.value })}
                required={field === "name"}
              />
            </label>
          ))}
        </div>
        <label>
          {t("hsClassifier.playground.fields.description")}
          <textarea
            className="mod-page__input"
            rows={2}
            value={draft.description}
            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
          />
        </label>
        <div className="mod-page__form-row">
          <button type="submit" className="mod-page__btn mod-page__btn--primary">
            {t("hsClassifier.playground.run")}
          </button>
        </div>
      </form>
      {submitted ? (
        <div className="kyb-sub">
          <HsClassifierPanel
            key={runCount}
            input={submitted}
            showVotes
            onResult={() => qc.invalidateQueries({ queryKey: getHscodesModeratorRunsRetrieveQueryKey() })}
          />
        </div>
      ) : null}
    </section>
  );
};

const RunLog = () => {
  const { t } = useTranslation(["moderator", "trade", "common"]);
  const [filter, setFilter] = useState<RunFilter>("uncorrected");
  const [page, setPage] = useState(1);
  const params: HscodesModeratorRunsRetrieveParams = { page, page_size: 20 };
  if (filter !== "all") params.corrected = filter === "corrected";
  const { data, isLoading, error } = useHscodesModeratorRunsRetrieve(params);
  const runs = unwrapPaginated<ClassificationRun>(data);
  const hasNext = Boolean((data as { next?: string | null } | undefined)?.next);

  return (
    <section>
      <div className="kyb-tabs" role="tablist">
        {(["uncorrected", "corrected", "all"] as const).map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={filter === key}
            className={`kyb-tabs__tab ${filter === key ? "kyb-tabs__tab--active" : ""}`}
            onClick={() => {
              setFilter(key);
              setPage(1);
            }}
          >
            {t(`hsClassifier.runs.filter.${key}`)}
          </button>
        ))}
      </div>
      <p className="kyb-section__help">{t("hsClassifier.runs.help")}</p>
      {isLoading ? <div className="mod-page__loading">{t("common:states.loading")}</div> : null}
      {error ? <div className="mod-page__error">{t("hsClassifier.runs.loadError")}</div> : null}
      {!isLoading && runs.length === 0 ? <div className="mod-page__empty">{t("hsClassifier.runs.empty")}</div> : null}
      {runs.map((run) => (
        <RunCard key={run.id} run={run} />
      ))}
      <div className="mod-page__toolbar">
        <button
          type="button"
          className="mod-page__btn mod-page__btn--ghost"
          disabled={page <= 1}
          onClick={() => setPage((p) => p - 1)}
        >
          {t("common:actions.previous")}
        </button>
        <button
          type="button"
          className="mod-page__btn mod-page__btn--ghost"
          disabled={!hasNext}
          onClick={() => setPage((p) => p + 1)}
        >
          {t("common:actions.next")}
        </button>
      </div>
    </section>
  );
};

const RunCard = ({ run }: { run: ClassificationRun }) => {
  const { t } = useTranslation(["moderator", "trade", "common"]);
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState(run.correctedCode || run.chosenCode || run.resultCode || "");
  const [note, setNote] = useState("");
  const correct = useHscodesModeratorRunsCorrectCreate({
    mutation: { onSuccess: () => qc.invalidateQueries({ queryKey: getHscodesModeratorRunsRetrieveQueryKey() }) },
  });
  const inputs = (run.inputs ?? {}) as Record<string, string | null | undefined>;
  const votes = (run.votes ?? {}) as Record<string, Ballot>;
  const statusChip =
    run.status === "classified"
      ? "mod-page__chip--success"
      : run.status === "needs_answer"
        ? "mod-page__chip--warning"
        : "mod-page__chip--danger";

  return (
    <article className="kyb-section">
      <div className="kyb-provider__head">
        <div>
          <h3 className="kyb-section__title">
            {inputs.name || EMPTY_VALUE}
            <span className={`mod-page__chip ${statusChip}`}>{t(`hsClassifier.runs.status.${run.status}`)}</span>
            <span className="mod-page__chip">{t(`hsClassifier.runs.source.${run.source}`)}</span>
          </h3>
          <p className="mod-page__card-meta">
            {formatDateTime(run.createdAt)} · {t("hsClassifier.runs.duration", { ms: run.durationMs })}
            {inputs.within ? ` · ${t("hsClassifier.runs.within", { code: inputs.within })}` : ""}
          </p>
          <p className="mod-page__card-meta">
            {t("hsClassifier.runs.result", {
              code: run.resultCode || EMPTY_VALUE,
              depth: run.resultDepth ? t(`trade:depth.${hsDepthKey(run.resultDepth)}`) : EMPTY_VALUE,
              confidence: run.confidence === null ? EMPTY_VALUE : formatPercent(run.confidence * 100),
            })}
            {run.chosenCode ? ` · ${t("hsClassifier.runs.chosen", { code: run.chosenCode })}` : ""}
          </p>
          {run.correctedCode ? (
            <p className="mod-page__card-meta">
              <span className="mod-page__chip mod-page__chip--success">
                {run.correctedCode === run.resultCode
                  ? t("hsClassifier.runs.confirmed")
                  : t("hsClassifier.runs.corrected", { code: run.correctedCode })}
              </span>
              {run.correctedBy ? t("hsClassifier.runs.by", { name: run.correctedBy }) : ""}
              {run.correctionNote ? ` — ${run.correctionNote}` : ""}
            </p>
          ) : null}
        </div>
        <button type="button" className="mod-page__btn mod-page__btn--ghost" onClick={() => setOpen((v) => !v)}>
          {open ? t("hsClassifier.runs.hide") : t("hsClassifier.runs.details")}
        </button>
      </div>

      {open ? (
        <>
          {inputs.description ? <p className="kyb-note">{inputs.description}</p> : null}
          <div className="kyb-sub kyb-scroll">
            <table className="kyb-table">
              <thead>
                <tr>
                  <th>{t("trade:classifier.votes.voter")}</th>
                  <th>{t("trade:classifier.votes.weight")}</th>
                  <th>{t("trade:classifier.votes.ballot")}</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(votes).map(([voter, ballot]) => (
                  <tr key={voter}>
                    <td>{isKnownVoter(voter) ? t(`trade:voter.${voter}`) : voter}</td>
                    <td>{ballot.weight}</td>
                    <td>
                      {ballot.skipped ? t("trade:classifier.votes.skipped", { why: ballot.skipped }) : null}
                      {ballot.error ? <span className="mod-page__inline-error">{ballot.error}</span> : null}
                      {(ballot.votes ?? []).map((v) => (
                        <span key={v.code} className="mod-page__chip">
                          {v.code}
                        </span>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="kyb-sub kyb-form">
            <p className="kyb-note">{t("hsClassifier.runs.correctHelp")}</p>
            <div className="mod-page__form-row">
              <input
                className="mod-page__input"
                style={{ maxWidth: 160 }}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                aria-label={t("hsClassifier.runs.code")}
              />
              <input
                className="mod-page__input"
                placeholder={t("hsClassifier.runs.note")}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>
            <div className="mod-page__form-row">
              {run.resultCode ? (
                <button
                  type="button"
                  className="mod-page__btn mod-page__btn--ghost"
                  disabled={correct.isPending}
                  onClick={() => correct.mutate({ id: run.id, data: { code: run.resultCode, note: note.trim() } })}
                >
                  {t("hsClassifier.runs.confirm", { code: run.resultCode })}
                </button>
              ) : null}
              <button
                type="button"
                className="mod-page__btn mod-page__btn--primary"
                disabled={!code.trim() || correct.isPending}
                onClick={() => correct.mutate({ id: run.id, data: { code: code.trim(), note: note.trim() } })}
              >
                {t("hsClassifier.runs.saveCorrection")}
              </button>
            </div>
            {correct.isError ? (
              <div className="mod-page__inline-error">
                {getApiErrorMessage(correct.error, t("hsClassifier.runs.correctError"))}
              </div>
            ) : null}
          </div>
        </>
      ) : null}
    </article>
  );
};

export default ModeratorHsClassifierPage;
